import React from "react";
import {
  Animated,
  Easing,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

import { ADMIN_TOKENS } from "../../../styles/adminTokens";

type BottomTrayModalProps = {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxContentWidth?: number;
};

const BottomTrayModal: React.FC<BottomTrayModalProps> = ({
  visible,
  onClose,
  title,
  children,
  footer,
  maxContentWidth = 700,
}) => {
  const animation = React.useRef(new Animated.Value(visible ? 1 : 0)).current;
  const [isRendered, setIsRendered] = React.useState<boolean>(visible);
  const [showContent, setShowContent] = React.useState<boolean>(visible);

  React.useEffect(() => {
    if (visible) {
      setIsRendered(true);
      setShowContent(false);
      animation.stopAnimation();
      Animated.timing(animation, {
        toValue: 1,
        duration: ADMIN_TOKENS.motion.standard,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      }).start();
      requestAnimationFrame(() => {
        setShowContent(true);
      });
      return;
    }

    if (!isRendered) {
      return;
    }

    animation.stopAnimation();
    Animated.timing(animation, {
      toValue: 0,
      duration: ADMIN_TOKENS.motion.standard,
      easing: Easing.inOut(Easing.ease),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        setShowContent(false);
        setIsRendered(false);
      }
    });
  }, [animation, isRendered, visible]);

  const backdropOpacity = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, ADMIN_TOKENS.opacity.muted],
  });
  const trayTranslateY = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [420, 0],
  });

  if (!isRendered) {
    return null;
  }

  return (
    <Modal transparent visible animationType="none" onRequestClose={onClose}>
      <View style={styles.backdropRoot}>
        <Animated.View
          pointerEvents="none"
          style={[styles.overlay, { opacity: backdropOpacity }]}
        />

        <Pressable
          style={styles.backdropTapArea}
          onPress={onClose}
          accessibilityLabel="Close tray"
        />

        <Animated.View
          style={[
            styles.tray,
            {
              maxWidth: maxContentWidth,
              transform: [{ translateY: trayTranslateY }],
            },
          ]}
        >
          <View style={styles.headerRow}>
            <Text style={styles.titleText}>{title || ""}</Text>
            <Pressable
              style={styles.closeButton}
              onPress={onClose}
              accessibilityLabel="Close tray"
            >
              <Ionicons
                name="close"
                size={16}
                color={ADMIN_TOKENS.color.textSecondary}
              />
            </Pressable>
          </View>

          {showContent ? children : null}

          {showContent && footer ? (
            <View style={styles.footer}>{footer}</View>
          ) : null}
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdropRoot: {
    flex: 1,
    justifyContent: "flex-end",
  },
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: ADMIN_TOKENS.color.textPrimary,
  },
  backdropTapArea: {
    flex: 1,
  },
  tray: {
    alignSelf: "center",
    width: "100%",
    borderTopLeftRadius: ADMIN_TOKENS.radius.lg,
    borderTopRightRadius: ADMIN_TOKENS.radius.lg,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.border,
    backgroundColor: ADMIN_TOKENS.color.surface,
    ...ADMIN_TOKENS.shadow.card,
    paddingHorizontal: ADMIN_TOKENS.spacing.lg,
    paddingTop: ADMIN_TOKENS.spacing.md,
    paddingBottom: ADMIN_TOKENS.spacing.lg,
    gap: ADMIN_TOKENS.spacing.md,
    maxHeight: "85%",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ADMIN_TOKENS.spacing.sm,
  },
  titleText: {
    color: ADMIN_TOKENS.color.textPrimary,
    fontFamily: ADMIN_TOKENS.font.semibold,
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
  },
  closeButton: {
    minHeight: 28,
    minWidth: 28,
    borderRadius: ADMIN_TOKENS.radius.sm,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.borderMuted,
    backgroundColor: ADMIN_TOKENS.color.surface,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: ADMIN_TOKENS.spacing.xs,
    paddingVertical: ADMIN_TOKENS.spacing.xs,
  },
  footer: {
    marginTop: ADMIN_TOKENS.spacing.xs,
  },
});

export default BottomTrayModal;
